/**
 * Renderer HTML seguro para mensajes de Sol.
 *
 * Sol responde con un subset HTML básico (b, i, code, a, br) para que el mismo
 * texto sirva en Telegram (parseMode='HTML') y en la web. El frontend web
 * necesita parsear ese subset de forma SEGURA — no podemos pasar el texto
 * directo a dangerouslySetInnerHTML porque Sol podría producir tags arbitrarios
 * (o un user malintencionado podría inyectar HTML vía un prospect cargado).
 *
 * Algoritmo:
 *   1. Escapamos TODO el HTML (input → texto plano).
 *   2. Reintroducimos solo los tags de la whitelist, reemplazando las
 *      versiones escapadas (`&lt;b&gt;`) por tags reales (`<b>`).
 *   3. Para `<a>` reescribimos el href y forzamos target="_blank" + rel seguro.
 *   4. Los `\n` los reemplazamos por `<br>` para preservar formato de líneas.
 *
 * Cualquier tag que NO esté en la whitelist queda como texto plano (escapado).
 */

const ALLOWED_INLINE = ['b', 'strong', 'i', 'em', 'u', 's', 'code', 'pre'] as const;

function escapeHtml(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Sanitiza una URL para usar en href. Acepta:
 *   - http(s)://...   externo, abrirá target=_blank
 *   - mailto:...      idem
 *   - /...            ruta interna del producto, React Router la maneja
 *
 * Si la URL no matchea, devuelve null (el link queda como texto plano).
 *
 * Devuelve también si es ruta interna para que el caller decida target.
 */
function sanitizeHref(raw: string): { safe: string; internal: boolean } | null {
    const trimmed = raw.trim();
    if (/^(https?:\/\/|mailto:)/i.test(trimmed)) {
        return { safe: trimmed.replace(/"/g, '&quot;'), internal: false };
    }
    // Ruta interna absoluta — solo aceptamos `/algo`, no `//evil.com` (protocol-relative).
    if (/^\/(?!\/)/.test(trimmed)) {
        return { safe: trimmed.replace(/"/g, '&quot;'), internal: true };
    }
    return null;
}

/**
 * Pre-pase liviano markdown → HTML para texto que puede venir con markdown
 * (ej. el LLM a veces emite `**negrita**` pese a pedirle HTML, o un user tipea
 * markdown). Convierte un subset SEGURO al HTML whitelist de Sol, para después
 * pasar por `renderSolHtmlSafe`. NO toca underscores ni `*` simple (riesgo de
 * falsos positivos en emails, IDs, multiplicaciones); solo casos inequívocos:
 *   `code` → <code> · **bold** → <b> · [texto](url) → <a>
 */
export function markdownLiteToHtml(text: string): string {
    if (!text) return '';
    return text
        .replace(/`([^`\n]+)`/g, '<code>$1</code>')
        .replace(/\*\*([^*\n]+)\*\*/g, '<b>$1</b>')
        .replace(
            /\[([^\]\n]+)\]\((https?:\/\/[^)\s]+|\/[^)\s]*)\)/g,
            '<a href="$2">$1</a>',
        );
}

export function renderSolHtmlSafe(text: string): string {
    if (!text) return '';

    // 1. Escape global.
    let html = escapeHtml(text);

    // 2. Reintroducir tags inline simples (sin atributos).
    for (const tag of ALLOWED_INLINE) {
        const openRe = new RegExp(`&lt;${tag}&gt;`, 'gi');
        const closeRe = new RegExp(`&lt;/${tag}&gt;`, 'gi');
        html = html.replace(openRe, `<${tag}>`).replace(closeRe, `</${tag}>`);
    }

    // 3. <br> auto-cerrado (Telegram a veces lo manda).
    html = html.replace(/&lt;br\s*\/?&gt;/gi, '<br>');

    // 4. <a href="..."> — parseamos el atributo con cuidado.
    //    Para links externos: target=_blank + rel seguro.
    //    Para rutas internas (/...): data-internal="true" para que SolHtmlInline
    //    intercepte clicks y navegue con react-router (sin full reload).
    html = html.replace(
        /&lt;a\s+href=(?:&quot;|&#39;)([^&]*?)(?:&quot;|&#39;)&gt;([\s\S]*?)&lt;\/a&gt;/gi,
        (_full, href: string, inner: string) => {
            const sanitized = sanitizeHref(href);
            if (!sanitized) return inner; // sin link, solo texto
            if (sanitized.internal) {
                return `<a href="${sanitized.safe}" data-sol-internal="true" class="text-primary underline">${inner}</a>`;
            }
            return `<a href="${sanitized.safe}" target="_blank" rel="noopener noreferrer" class="text-primary underline">${inner}</a>`;
        },
    );

    // 5. Saltos de línea.
    html = html.replace(/\n/g, '<br>');

    // 6. Colapsar saltos excesivos. El modelo a veces emite 3+ saltos seguidos
    //    (renglones en blanco de más) que en la burbuja se ven como huecos
    //    enormes. Dejamos como máximo UN renglón en blanco (dos <br>).
    html = html.replace(/(?:<br>\s*){3,}/gi, '<br><br>');
    // Sin <br> colgando al principio/final.
    html = html.replace(/^(?:\s*<br>\s*)+/i, '').replace(/(?:\s*<br>\s*)+$/i, '');

    return html;
}
